<script setup>
import { onMounted, nextTick, watch, ref, computed, onBeforeUnmount  } from "vue";
import Chart from "chart.js/auto";
import axios from "axios";
import { BACK_END_URL } from '@/utilities/config';

const chartRef = ref(null);
const chartInstance = ref(null); 
const isLoading = ref(true); 
const props = defineProps({
  provinceId: String, // ID de provincia desde el padre
});

// Mapeo de provincias
const provinceMap = {
  "1": "San José",
  "2": "Alajuela",
  "3": "Cartago",
  "4": "Heredia",
  "5": "Guanacaste",
  "6": "Puntarenas",
  "7": "Limón",
  "8": "Extranjeros",
};

// Propiedad computada para obtener el nombre de la provincia
const provinceName = computed(() => {
  return provinceMap[props.provinceId] || "Desconocido"; 
});

// Obtiene data de la provincia desde el back-end
const fetchData = async (provinceId) => {
  if (!provinceId) {
    console.error("No province ID provided.");
    return { positivos: 0, negativos: 0, neutros: 0, noIdentificados: 0 };
  }

  const token = sessionStorage.getItem("token"); 

  try {
    const response = await axios.get(`${BACK_END_URL}/analytics/dashboard?provincia=${props.provinceId}`, {
      headers: {
        Authorization: `Bearer ${token}`, 
      },
    });
    const provinceData = response.data.estados[props.provinceId];

    const result = {
      positivos: provinceData.positivo || 0,
      negativos: provinceData.negativo || 0,
      neutros: provinceData.neutro || 0,
      noIdentificados: provinceData.no_identificado || 0,
    };

    return result;
  } catch (error) {
    console.error("Error fetching data:", error);
    return { positivos: 0, negativos: 0, neutros: 0, noIdentificados: 0 };
  }
};



// Funcion para actualizar el gráfico
const updateChart = async () => {
  if (!chartRef.value) {
    console.warn("Chart reference is null. Skipping chart update.");
    return;
  }

  isLoading.value = true;

  // Asegura que el canvas esté renderizado
  await nextTick();

  const ctx = chartRef.value.getContext("2d");

  const existingChart = Chart.getChart(chartRef.value);
  if (existingChart) {
    existingChart.destroy();
  }
  const { positivos, negativos, neutros, noIdentificados } = 
    await fetchData(props.provinceId);


  chartInstance.value = new Chart(ctx, {
    type: "bar",
    data: {
      labels: ["Positivo", "Negativo", "Neutro", "No Identificado"],
      datasets: [
        {
          backgroundColor: ["#2dce89", "#FF4767", "#344767", "#c3cfdb"],
          data: [positivos, negativos, neutros, noIdentificados],
        },
      ],
    },
    options: {
      indexAxis: "x",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        y: {
          grid: {
            drawBorder: false,
            display: true,
            drawOnChartArea: true,
            drawTicks: false,
            borderDash: [5, 5],
          },
          ticks: {
            display: true,
            padding: 10,
            color: "#9ca2b7",
          },
          title: {
            display: true,
            text: 'Votantes',
            color: "#9ca2b7",
          },
        },
        x: {
          grid: {
            drawBorder: false,
            display: false,
            drawOnChartArea: true,
            drawTicks: true,
          },
          ticks: {
            display: true,
            color: "#9ca2b7",
            padding: 10,
          },
          title: {
            display: true,
            text: 'Estados',
            color: "#9ca2b7",
          },
        },
      },
    },
  });

  isLoading.value = false;
};

// Revisa el ID de la provincia para actualizar el gráfico
watch(() => props.provinceId, async () => {
  await updateChart(); 
}, { immediate: true });

onBeforeUnmount(() => {
  if (chartInstance.value) {
    chartInstance.value.destroy();
  }
});

onMounted(updateChart);
</script>

<template>
  <div class="card h-100">
    <div class="p-3 card-header">
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="mb-0">Distribución por Estado en : {{ provinceName }}</h6>
        <div v-if="isLoading" class="spinner-border text-primary" role="status">
        </div>
      </div>
    </div>
    <div class="p-3 card-body">
      <div class="chart">
        <canvas ref="chartRef" class="chart-canvas" height="300"></canvas>
      </div>
    </div>
  </div>
</template>
